import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import Spinner from '@/components/Common/Spinner';
import { Stage } from '@/components/Stage/Stage';
import type { GetIssueSummaryResponseDTO } from '@/features/(signal)/signals/api';
import {
  DYNAMIC_FILTER_QUERY_KEY,
  parseIssueDetailDateSelection,
  useIssueListQuery,
} from '@/features/(signal)/signals/hooks';
import { parseSignalDatePreset } from '@/features/(signal)/signals/lib';
import type { SignalDateSelection } from '@/features/(signal)/signals/model';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import useUIStore from '@/stores/pure/UIStore';

import { resolveIssueDetailDateRange } from '../lib/issue-detail-date-range';
import { STATIC_ISSUE_SUMMARY_DATA_KEY } from '../model/constants';
import { useComputedIssue } from '../model/use-computed-issue';
import { CustomChartArea } from './custom-chart';
import { IssueDetailPropertyArea } from './issue-detail-property-area';
import { IssueDetailSummaryContent } from './issue-detail-summary';
import { SentimentPieChartArea } from './sentiment-pie-chart-area';
import { VolumeChangeChartArea } from './volume-change-chart-area';
import { WordCloudArea } from './word-cloud-area';

export const StaticIssueDetailPage = () => {
  const { parseStructuredFilter } = useStructuredFilter();
  const setSidebarHidden = useUIStore((state) => state.setSidebarHidden);

  const data: GetIssueSummaryResponseDTO = useMemo(() => {
    const dataString = localStorage.getItem(STATIC_ISSUE_SUMMARY_DATA_KEY);
    if (dataString) {
      return JSON.parse(dataString) as GetIssueSummaryResponseDTO;
    }
    return {
      body: '',
      references: [],
    };
  }, []);

  const { issue_name } = useParams();
  if (!issue_name) throw new Error('Issue name is required');

  const [searchParams] = useSearchParams();
  const dynamicFilterSetString = searchParams.get(DYNAMIC_FILTER_QUERY_KEY);
  const dynamicFilterSet = dynamicFilterSetString
    ? (JSON.parse(dynamicFilterSetString) as StructuredFilter)
    : null;

  const structuredFiltreQueryState = useStructuredFilterQuery(
    undefined,
    dynamicFilterSet ? parseStructuredFilter(dynamicFilterSet) : undefined,
  );

  const [datePreset] = useState<SignalDateSelection>(() =>
    parseIssueDetailDateSelection(searchParams),
  );

  const { unit } = parseSignalDatePreset(datePreset);

  const compositedFilter = useMemo(() => {
    const sFilter = new SFilter();
    sFilter.addFilterQueryFormats(
      structuredFiltreQueryState.filterQueryFormats,
    );

    const [startDate, endDate] = resolveIssueDetailDateRange({
      datePreset,
      mode: 'composited',
    });
    sFilter.setDateFilter({
      start_date: startDate.toJSON(),
      end_date: endDate.toJSON(),
    });

    return sFilter.get() || {};
  }, [
    datePreset,
    JSON.stringify(structuredFiltreQueryState.filterQueryFormats),
    searchParams,
  ]);

  const chartFilter = useMemo(() => {
    const sFilter = new SFilter();
    sFilter.addFilterQueryFormats(
      structuredFiltreQueryState.filterQueryFormats,
    );

    const [startDate, endDate] = resolveIssueDetailDateRange({
      datePreset,
      mode: 'selected',
    });
    sFilter.setDateFilter({
      start_date: startDate.toJSON(),
      end_date: endDate.toJSON(),
    });

    return sFilter.get() || {};
  }, [
    datePreset,
    JSON.stringify(structuredFiltreQueryState.filterQueryFormats),
    searchParams,
  ]);

  const {
    issueAggregates,
    originIssues,
    signals,
    aggregateFetching,
    originIssueFetching,
  } = useIssueListQuery({
    filterParams: compositedFilter || {},
    period: unit,
    issue: issue_name,
  });

  const compositedIssue = useComputedIssue({
    aggregatedIssue: issueAggregates[0],
    originIssues,
    issueName: issue_name,
    datePreset,
    signals,
  });

  useEffect(() => {
    setSidebarHidden(true);
  }, [setSidebarHidden]);

  return (
    <div id="static-root">
      <div className="flex justify-center w-full pt-4">
        <Stage className="flex flex-col h-full w-full max-w-200 px-4">
          <Stage.Scene status={aggregateFetching || originIssueFetching}>
            <Spinner />
          </Stage.Scene>
          <Stage.Scene status={!!compositedIssue} data={compositedIssue}>
            {(issue) => (
              <div className="flex flex-col gap-16 pb-40">
                <IssueDetailPropertyArea
                  issue={issue}
                  filterSet={compositedFilter}
                  selectedPeriodFilterSet={chartFilter}
                  datePreset={datePreset}
                />
                <IssueDetailSummaryContent
                  summary={data.body}
                  references={data.references}
                />
                <VolumeChangeChartArea
                  issue={issue}
                  filterSet={compositedFilter}
                  datePreset={datePreset}
                />
                <WordCloudArea
                  issue={issue}
                  datePreset={datePreset}
                  filterSet={compositedFilter}
                />
                <SentimentPieChartArea
                  issue={issue}
                  datePreset={datePreset}
                  filterSet={compositedFilter}
                />
                <CustomChartArea
                  issue={issue}
                  datePreset={datePreset}
                  filterSet={compositedFilter}
                />
              </div>
            )}
          </Stage.Scene>
        </Stage>
      </div>
    </div>
  );
};
